<script lang="ts">
/** The open sheets, the top sheet last. A sheet opened from another sheet stacks on it. */
const openSheets: symbol[] = [];
</script>

<script lang="ts" setup>
import { useDisplay, useLayout } from 'vuetify';

interface Props {
  modelValue: boolean;
  /** With `resizeKey`, a number is the width before the user drags. */
  width?: string | number;
  persistent?: boolean;
  scrim?: boolean | string;
  /** Runs before Escape or a scrim click closes the sheet. A false result keeps it open. */
  beforeClose?: () => boolean | Promise<boolean>;
  /**
   * Lets the user drag the left edge. The width stays in localStorage under this key. The width
   * rules come from RightDockPanel of admin-web-common.
   */
  resizeKey?: string;
  /** Covers the full width of the window. The sheet keeps its width for the restore. */
  maximized?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  width: '80%',
  persistent: false,
  scrim: true,
  resizeKey: '',
  maximized: false,
});

// ── Resize ───────────────────────────────────────────────────────────────────
const MIN_WIDTH = 480;
const MAX_WIDTH = 4000;
// The window keeps this width on the left of the sheet, so the user can still click the scrim.
const MIN_REST_WIDTH = 120;
// Below this window width, a resizable sheet covers the full width.
const FULLSCREEN_BELOW = 600;
const KEY_STEP = 16;

const display = useDisplay();
// The sheet stops at the left edge of a docked right drawer, for example the Suge chat panel, so
// the user can read and use the chat while a sheet is open. A closed or a temporary drawer takes
// no layout space and gives 0.
const layout = useLayout();
const dockedRight = computed(() => layout.mainRect.value.right);
// The window width that the sheet and its scrim can cover.
const viewportWidth = computed(() => (display.width.value || window.innerWidth) - dockedRight.value);
const fullscreen = computed(
  () => props.maximized || (props.resizeKey !== '' && viewportWidth.value < FULLSCREEN_BELOW),
);
const resizable = computed(() => props.resizeKey !== '' && !fullscreen.value);
const limits = computed(() => dockPanelLimits(MIN_WIDTH, MAX_WIDTH, viewportWidth.value, MIN_REST_WIDTH));
const storageKey = computed(() => `sheet-width:${props.resizeKey}`);

const storage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

// The width that the user chose. A narrow window shows less, but keeps this value.
const userWidth = ref(0);
watch(
  storageKey,
  () => {
    const fallback = typeof props.width === 'number' ? props.width : MIN_WIDTH;
    userWidth.value = dockPanelReadWidth(storage(), storageKey.value, fallback, { min: MIN_WIDTH, max: MAX_WIDTH });
  },
  { immediate: true },
);

const contentWidth = computed(() => {
  if (fullscreen.value) return '100%';
  if (resizable.value) return `${dockPanelClampWidth(userWidth.value, limits.value)}px`;
  return typeof props.width === 'number' ? `${props.width}px` : props.width;
});

const dragging = ref(false);
let dragStart: { x: number; width: number } | null = null;

const saveWidth = (value: number) => {
  userWidth.value = dockPanelWriteWidth(storage(), storageKey.value, value, limits.value);
};

const onPointerMove = (event: PointerEvent) => {
  if (!dragStart) return;
  userWidth.value = dockPanelDragWidth(dragStart.width, dragStart.x, event.clientX, limits.value);
};

const endDrag = () => {
  dragStart = null;
  dragging.value = false;
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', stopDrag);
};

const stopDrag = () => {
  if (!dragStart) return;
  endDrag();
  saveWidth(userWidth.value);
};

const startDrag = (event: PointerEvent) => {
  if (event.button !== 0) return;
  event.preventDefault();
  dragStart = { x: event.clientX, width: dockPanelClampWidth(userWidth.value, limits.value) };
  dragging.value = true;
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', stopDrag);
};

const onHandleKey = (event: KeyboardEvent) => {
  const delta = event.key === 'ArrowLeft' ? KEY_STEP : event.key === 'ArrowRight' ? -KEY_STEP : 0;
  if (delta === 0) return;
  event.preventDefault();
  saveWidth(dockPanelClampWidth(userWidth.value, limits.value) + delta);
};

// An unmount during a drag removes the listeners and stores nothing.
onBeforeUnmount(endDrag);

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const close = async () => {
  if (props.persistent) return;
  if (props.beforeClose && !(await props.beforeClose())) return;
  isOpen.value = false;
};

const handleBackdropClick = (e: MouseEvent) => {
  if (e.target === e.currentTarget && !props.persistent) {
    close();
  }
};

const handleKeydown = (e: KeyboardEvent) => {
  // A sheet can open another sheet. Only the top sheet takes Escape.
  if (openSheets.at(-1) !== sheetId) return;
  // The docked chat panel sits beside the sheet, so Escape in the chat belongs to the chat. The
  // full-width chat on a narrow window is not docked and gets no exception.
  if (e.target instanceof Element && e.target.closest('.right-dock-panel:not(.right-dock-panel--fullscreen)')) {
    return;
  }
  // A dialog or a menu above the sheet takes Escape for itself.
  if (document.querySelector('.v-overlay--active')) return;
  if (e.key === 'Escape' && !props.persistent) {
    close();
  }
};

const sheetId = Symbol('right-sheet');

const enter = () => {
  if (openSheets.includes(sheetId)) return;
  openSheets.push(sheetId);
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';
  window.addEventListener('keydown', handleKeydown);
};

const leave = () => {
  const index = openSheets.indexOf(sheetId);
  if (index === -1) return;
  openSheets.splice(index, 1);
  window.removeEventListener('keydown', handleKeydown);
  // A sheet below this one still needs the page to stay still.
  if (openSheets.length === 0) {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  }
};

// A list can open the sheet from the URL on the first render, thus the watcher also runs at once.
watch(isOpen, open => (open ? enter() : leave()), { immediate: true });

onUnmounted(leave);
</script>

<template>
  <Teleport to="body">
    <Transition name="right-sheet">
      <div
        v-if="isOpen"
        class="right-sheet-overlay"
        :class="{ 'right-sheet-overlay--scrim': scrim }"
        :style="{ right: `${dockedRight}px` }"
        @click="handleBackdropClick"
      >
        <div
          class="right-sheet-panel"
          :style="{ width: contentWidth }"
          @click.stop
        >
          <div
            v-if="resizable"
            class="right-sheet-handle"
            :class="{ 'right-sheet-handle--dragging': dragging }"
            role="separator"
            aria-orientation="vertical"
            :aria-valuenow="dockPanelClampWidth(userWidth, limits)"
            :aria-valuemin="limits.min"
            :aria-valuemax="limits.max"
            tabindex="0"
            @pointerdown="startDrag"
            @keydown="onHandleKey"
          />
          <div
            class="right-sheet-content bg-dark-20"
            :class="{ 'right-sheet-content--dragging': dragging }"
          >
            <slot />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.right-sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* Above the app bar and the drawers, below every Vuetify overlay (2000 and up). A menu or a
     dialog of the sheet then stacks above it in the order that Vuetify gives. */
  z-index: 1900;
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  overflow: hidden;
  /* The same motion as a Vuetify drawer, thus the sheet follows the chat panel when it opens or
     closes. */
  transition: right 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* The drawer has no transition during a drag of its edge. Without this rule, the sheet trails the
   pointer. */
:global(body:has(.right-dock-panel--dragging)) .right-sheet-overlay {
  transition: none;
}

.right-sheet-overlay--scrim {
  background-color: rgba(0, 0, 0, 0.5);
}

/* The open and the close transitions move this box, thus the grip moves with the content. */
.right-sheet-panel {
  position: relative;
  height: 100vh;
}

/* A strip on the left edge of the sheet. It sits outside the content, so it does not scroll. */
.right-sheet-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -4px;
  width: 8px;
  z-index: 1;
  cursor: col-resize;
}

.right-sheet-handle:hover,
.right-sheet-handle:focus-visible,
.right-sheet-handle--dragging {
  background: rgba(var(--v-theme-primary), 0.35);
  outline: none;
}

/* A grip on the middle of the edge tells the user that the sheet has a resizable width. */
.right-sheet-handle::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 14px;
  height: 40px;
  transform: translate(-50%, -50%);
  border: 1px solid var(--color-stroke-bold);
  border-radius: 7px;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.right-sheet-handle::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 6px;
  height: 16px;
  transform: translate(-50%, -50%);
  border-inline: 2px solid rgb(var(--v-theme-dark-500));
}

.right-sheet-handle:hover::before,
.right-sheet-handle:focus-visible::before,
.right-sheet-handle--dragging::before {
  border-color: rgb(var(--v-theme-primary));
}

.right-sheet-content--dragging {
  user-select: none;
}

.right-sheet-content {
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  position: relative;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

/* Slide in from right animation */
.right-sheet-enter-active,
.right-sheet-leave-active {
  transition: opacity 0.3s ease;
}

.right-sheet-enter-active .right-sheet-panel,
.right-sheet-leave-active .right-sheet-panel {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.right-sheet-enter-from {
  opacity: 0;
}

.right-sheet-enter-from .right-sheet-panel {
  transform: translateX(100%);
}

.right-sheet-enter-to {
  opacity: 1;
}

.right-sheet-enter-to .right-sheet-panel {
  transform: translateX(0);
}

.right-sheet-leave-from {
  opacity: 1;
}

.right-sheet-leave-from .right-sheet-panel {
  transform: translateX(0);
}

.right-sheet-leave-to {
  opacity: 0;
}

.right-sheet-leave-to .right-sheet-panel {
  transform: translateX(100%);
}
</style>

<style>
/* The top header of every sheet has the height of the app bar, so the header borders of the page,
   the sheet and the chat panel meet in one line. Vuetify sets the toolbar height inline, thus
   the rule needs !important. This block is not scoped, because the header comes from the slot. */
.right-sheet-content > * > .v-toolbar:first-child > .v-toolbar__content {
  height: 64px !important;
}

/* The help sheet shows DocsEmbeddedView, which reads its bar height from this variable. The bar
   height includes its 1px bottom border, and the app bar has 64px plus its border. */
.right-sheet-content .docs-app {
  --docs-app-bar-height: 65px;
}
</style>
